import { useMemo, useState } from 'react';
import { Bell, ListChecks } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
} from '@/components/ui/combobox';
import type {
  ChecklistItemKind,
  OrgEventChecklistItemDef,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import { checklistSubtitle, filterChecklistItems } from './checklistSubtitles';

const NEW_ORG_ACTION = '__new_org_action__';
const NEW_SEND_NOTIFICATION = '__new_send_notification__';

const CREATE_OPTIONS = [
  { value: NEW_ORG_ACTION, label: 'New Task', kind: 'org_action' as const },
  {
    value: NEW_SEND_NOTIFICATION,
    label: 'New Notification Template',
    kind: 'send_notification' as const,
  },
];

/** Sentinels are not real ids, so a match is unambiguous. */
function createKindFor(value: string): ChecklistItemKind | null {
  return CREATE_OPTIONS.find((opt) => opt.value === value)?.kind ?? null;
}

/**
 * Inline add row for the response checklist table: one combobox that either
 * pulls an existing item out of the library or starts a blank template. The two
 * create entries are pinned above the library, which is why filtering is done
 * here instead of by the combobox - Base UI would filter them out as soon as
 * the query stopped matching their labels.
 */
export function ChecklistAddRow({
  library,
  userNameById,
  onPickLibraryItem,
  onCreateNew,
  onCancel,
}: {
  /** Library items not already on the rule. */
  library: OrgEventChecklistItemDef[];
  userNameById: Map<string, string>;
  onPickLibraryItem: (item: OrgEventChecklistItemDef) => void;
  onCreateNew: (kind: ChecklistItemKind) => void;
  onCancel: () => void;
}) {
  const [query, setQuery] = useState('');

  const matches = useMemo(
    () => filterChecklistItems(library, query, userNameById),
    [library, query, userNameById]
  );

  const handleSelect = (value: unknown) => {
    if (typeof value !== 'string' || !value) return;
    const kind = createKindFor(value);
    if (kind) {
      onCreateNew(kind);
      return;
    }
    const picked = library.find((item) => item.id === value);
    if (picked) onPickLibraryItem(picked);
  };

  return (
    <div className="tw:flex tw:items-start tw:gap-2">
      <div className="tw:min-w-0 tw:flex-1">
        <Combobox
          value={null}
          filter={null}
          inputValue={query}
          onInputValueChange={(next) => setQuery(String(next ?? ''))}
          onValueChange={handleSelect}
        >
          <ComboboxInput
            autoFocus
            placeholder="Search the library, or create a new item…"
            aria-label="Search the library, or create a new item"
            className="tw:w-full"
            onKeyDown={(e) => {
              if (e.key === 'Escape') onCancel();
            }}
          />
          <ComboboxContent className="dark tw:w-[min(40rem,var(--available-width))] tw:min-w-[min(24rem,var(--available-width))] tw:max-w-[min(40rem,var(--available-width))]">
            <ComboboxList className="tw:max-h-[min(24rem,var(--available-height))]">
              <ComboboxGroup>
                <ComboboxLabel>Create new</ComboboxLabel>
                {CREATE_OPTIONS.map((opt) => (
                  <ComboboxItem
                    key={opt.value}
                    value={opt.value}
                    className="tw:h-auto tw:min-h-8 tw:items-center tw:py-1.5"
                  >
                    {opt.kind === 'send_notification' ? (
                      <Bell className="tw:h-3.5 tw:w-3.5" />
                    ) : (
                      <ListChecks className="tw:h-3.5 tw:w-3.5" />
                    )}
                    {opt.label}
                  </ComboboxItem>
                ))}
              </ComboboxGroup>
              <ComboboxSeparator />
              <ComboboxGroup>
                <ComboboxLabel>Library</ComboboxLabel>
                {matches.length === 0 ? (
                  <p className="tw:m-0 tw:px-2 tw:py-1.5 tw:text-xs tw:text-muted-foreground">
                    {library.length === 0
                      ? 'All library items are already added.'
                      : `No library items match “${query}”.`}
                  </p>
                ) : (
                  matches.map((item) => (
                    <ComboboxItem
                      key={item.id}
                      value={item.id}
                      className="tw:h-auto tw:min-h-8 tw:items-start tw:whitespace-normal tw:py-2"
                    >
                      <span className="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-0.5 tw:overflow-hidden">
                        <span className="tw:break-words tw:leading-snug">
                          {item.label}
                        </span>
                        <span className="tw:break-words tw:text-[10px] tw:leading-snug tw:text-muted-foreground">
                          {checklistSubtitle(item, userNameById)}
                        </span>
                      </span>
                    </ComboboxItem>
                  ))
                )}
              </ComboboxGroup>
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      </div>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="tw:shrink-0"
        onClick={onCancel}
      >
        Cancel
      </Button>
    </div>
  );
}
